import { Check } from '@mui/icons-material';
import { Box, ListItem, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
    ORDER_VERSION_PREFERENCE_OPTIONS,
    OrderVersionPreference,
} from 'shared/const/orderVersionPreference';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';

import { getOrderVersionPreference } from '../../model/selectors/orderVersionPreferenceSelectors';
import { setOrderVersionPreference } from '../../model/services/setOrderVersionPreference/setOrderVersionPreference';

const listItemStyles = {
    sx: {
        padding: 0,
        ':hover': {
            bgcolor: 'action.hover',
            cursor: 'pointer',
        },
    },
};

const preferenceLabelKeys: Record<OrderVersionPreference, string> = {
    auto: 'Order version auto',
    v1: 'Order version v1',
    v2: 'Order version v2',
};

export const OrderVersionPreferenceMenuItems = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const preference = useSelector(getOrderVersionPreference);

    const onSelectPreference = (
        event: React.MouseEvent,
        nextPreference: OrderVersionPreference,
    ) => {
        event.stopPropagation();

        if (nextPreference === preference) {
            return;
        }

        dispatch(setOrderVersionPreference(nextPreference));
    };

    return (
        <Box
            onClick={(event) => event.stopPropagation()}
            sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
                paddingTop: '8px',
                paddingBottom: '4px',
            }}
        >
            <Typography
                sx={{
                    padding: '0 16px 2px',
                    fontSize: '11px',
                    color: 'text.secondary',
                    letterSpacing: '0.02em',
                    textTransform: 'uppercase',
                }}
            >
                {t('Order version preference')}
            </Typography>

            {ORDER_VERSION_PREFERENCE_OPTIONS.map((option) => (
                <ListItem
                    key={option}
                    onClick={(event) => onSelectPreference(event, option)}
                    {...listItemStyles}
                    data-testclass={`order-version-preference-${option}`}
                >
                    <Box
                        sx={{
                            width: '100%',
                            minHeight: '28px',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0 16px 0 12px',
                        }}
                    >
                        <Check
                            sx={{
                                width: '16px',
                                height: '16px',
                                visibility:
                                    preference === option
                                        ? 'visible'
                                        : 'hidden',
                                color: 'primary.main',
                            }}
                        />
                        <Typography
                            sx={{
                                flex: 1,
                                fontSize: '13px',
                                marginLeft: '4px',
                                color: 'text.secondary',
                            }}
                        >
                            {t(preferenceLabelKeys[option])}
                        </Typography>
                    </Box>
                </ListItem>
            ))}
        </Box>
    );
};
